import { Component, Output, EventEmitter, model, computed, input, output, viewChild, ElementRef, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-search-input',
  templateUrl: './search-input.component.html',
  styleUrl: './search-input.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule],
})
export class SearchInputComponent<T> implements OnInit {
  // Two-way binding for search text using model signal
  readonly searchText = model<string>('');

  // Configuration inputs
  readonly placeholder = input<string>('Søg...');
  readonly ariaLabel = input<string>('Search');
  readonly autofocus = input<boolean>(false);
  readonly items = input<T[]>([]); // Items to search through
  readonly searchFields = input<(keyof T)[]>([]); // Fields to search in

  // Search function input - allows custom search logic
  readonly searchFunction = input<(items: T[], searchTerm: string) => T[]>();

  // Output events
  readonly filteredItemsChange = output<T[]>();
  readonly searchChange = output<string>();

  // ViewChild signal reference to the input element
  readonly searchInputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  // Computed signal for filtered items
  readonly filteredItems = computed(() => {
    const search = this.searchText().trim().toLowerCase();
    const items = this.items();

    if (!search || !items.length) {
      const result = items;
      this.filteredItemsChange.emit(result);
      return result;
    }

    let result: T[];

    const searchFunction = this.searchFunction();
    if (searchFunction) {
      // Use custom search function if provided
      result = searchFunction(items, search);
    } else if (this.searchFields().length > 0) {
      // Use field-based search
      result = items.filter(item => 
        this.searchFields().some(field => {
          const value = item[field];
          return value && String(value).toLowerCase().includes(search);
        })
      );
    } else {
      // Default: search all string properties
      result = items.filter(item => 
        Object.values(item as any).some(value => 
          value && typeof value === 'string' && value.toLowerCase().includes(search)
        )
      );
    }

    this.filteredItemsChange.emit(result);
    return result;
  });

  onSearchChange(value: string) {
    this.searchChange.emit(value);
    // Trigger computed signal evaluation
    this.filteredItems();
  }

  // Lifecycle hook to handle autofocus
  ngOnInit() {
    if (this.autofocus()) {
      // Use setTimeout to ensure the view is fully initialized
      setTimeout(() => {
        const inputElement = this.searchInputRef();
        inputElement?.nativeElement?.focus();
      });
    }
  }
}